import React, { useMemo } from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import {
  SlotRenderer,
  useHeroAnimation,
  useHeroAmbient,
} from "./heroModeUtils";
import "./heroModeAnimations.css";

const BP_CONFIG = {
  extra_large: { maxWidth: "55%", inset: 80, padding: 40, align: "start", blur: 16, bg: "rgba(255,255,255,0.12)" },
  large:       { maxWidth: "60%", inset: 64, padding: 32, align: "start", blur: 16, bg: "rgba(255,255,255,0.12)" },
  medium:      { maxWidth: "70%", inset: 40, padding: 28, align: "start", blur: 16, bg: "rgba(255,255,255,0.12)" },
  small:       { maxWidth: "calc(100% - 32px)", inset: 16, padding: 24, align: "center", blur: 12, bg: "rgba(255,255,255,0.15)" },
  extra_small: { maxWidth: "calc(100% - 24px)", inset: 12, padding: 20, align: "center", blur: 12, bg: "rgba(255,255,255,0.15)" },
};

const HeroCard = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  const isMobile = config.align === "center";

  const entrance = heroSlots?.animatedEntrance || "card-slide-up";
  const cardAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });
  // Card's ambient animation (float, shipped with ``card_animated``)
  // attaches to the card element itself — the glass-morph card gently
  // bobs up and down so the mode has a sense of life even after the
  // entrance settles. Ambient is mode-owned, independent of the
  // entrance toggle.
  const cardAmbientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  const cardStyle = useMemo(() => ({
    maxWidth: config.maxWidth,
    padding: config.padding,
    backdropFilter: `blur(${config.blur}px)`,
    WebkitBackdropFilter: `blur(${config.blur}px)`,
    backgroundColor: config.bg,
  }), [config]);

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      <Background
        bgImage={heroBase.bgImage}
        imageStyleClasses={heroBase.imageStyleClasses}
        imageInlineStyles={heroBase.imageInlineStyles}
        colorClass={heroBase.colorClass}
        colorStyle={heroBase.colorStyle}
        jsPatternLayer={heroBase.jsPatternLayer}
      />

      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={`${
          responsiveHeight ? "relative z-10 w-full" : "absolute inset-0 z-10"
        } flex ${isMobile ? "items-end justify-center" : "items-end justify-start"}`}
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        <div
          ref={cardAnim.ref}
          className={`rounded-2xl border border-white/10 ${cardAnim.className} ${cardAmbientClass}`}
          style={{
            ...cardStyle,
            ...cardAnim.style,
            margin: isMobile ? `0 auto ${config.inset}px` : `0 0 ${config.inset}px ${config.inset}px`,
          }}
        >
          <div className="flex flex-col gap-4">
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroCard);
